import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Briefcase,
  User,
  CreditCard,
  MonitorPlay,
  LogOut,
  FileText,
  HelpCircle,
  BarChart3,
  FileEdit,
  MessagesSquare,
  Settings,
  ChevronRight,
  Search,
  Bell,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import { useAuthStore, useUIStore } from "@/store";
import { useLogout } from "@/hooks";
import { cn } from "@/lib/utils";

/* ── Nav items ── */
const NAV_ITEMS = [
  { label: "Dashboard",      icon: LayoutDashboard, href: "/dashboard",      shortcut: "D" },
  { label: "Browse Jobs",    icon: Briefcase,       href: "/browse-jobs",    shortcut: "J" },
  { label: "My Jobs",        icon: FileText,        href: "/jobs",           shortcut: "M" },
  { label: "Job Profile",    icon: FileText,        href: "/job-profile",    shortcut: "P" },
  { label: "Questions",      icon: MessagesSquare,  href: "/job-questions",  shortcut: "Q" },
  { label: "Analytics",      icon: BarChart3,       href: "/analytics",      shortcut: "A" },
  { label: "Subscription",   icon: CreditCard,      href: "/subscription",   shortcut: "B" },
];

const BOTTOM_ITEMS = [
  { label: "Profile",  icon: User,       href: "/profile" },
  { label: "Settings", icon: Settings,   href: "/settings" },
  { label: "Help",     icon: HelpCircle, href: "https://www.applyiq.ai/help", external: true },
];

/* ── Rail item ── */
function RailItem({
  label, icon: Icon, href, external, badge, showLabel,
}: {
  label: string; icon: React.ElementType; href: string;
  external?: boolean; badge?: string; showLabel: boolean;
}) {
  const content = (isActive: boolean) => (
    <>
      <div className={cn(
        "relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group w-full",
        isActive
          ? "bg-[#6366F1] text-white shadow-sm"
          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100",
      )}>
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#6366F1] rounded-r-full" />
        )}
        <Icon className="h-4 w-4 flex-shrink-0" />
        <AnimatePresence>
          {showLabel && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.15 }}
              className="text-sm font-semibold whitespace-nowrap overflow-hidden flex-1"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
        {showLabel && badge && (
          <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#10B981] text-white">
            {badge}
          </span>
        )}
        {/* Tooltip when collapsed */}
        {!showLabel && (
          <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900 text-white text-xs rounded-xl whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 font-medium shadow-lg">
            {label}
            {badge && <span className="ml-2 text-[#10B981] font-mono text-[9px]">{badge}</span>}
          </div>
        )}
      </div>
    </>
  );

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {content(false)}
      </a>
    );
  }

  return (
    <NavLink to={href}>
      {({ isActive }) => content(isActive)}
    </NavLink>
  );
}

/* ── Sidebar rail ── */
function SideRail({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <motion.aside
      animate={{ width: expanded ? 240 : 64 }}
      transition={{ type: "spring", stiffness: 400, damping: 38 }}
      className="flex flex-col h-full flex-shrink-0 overflow-hidden bg-white border-r border-gray-200"
    >
      {/* Logo area */}
      <div
        className="flex items-center h-14 px-4 border-b border-gray-200 flex-shrink-0 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] text-white shadow-sm">
          <span className="font-display font-black text-sm">IQ</span>
        </div>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
              className="ml-3 flex-1 min-w-0 flex items-center justify-between"
            >
              <span className="font-display font-black text-base text-gray-900 tracking-tight">
                APPLY<span className="text-[#6366F1]">IQ</span>
                <span className="font-mono text-[9px] text-gray-400 ml-1">AI</span>
              </span>
              <ChevronRight className="h-4 w-4 text-gray-400 rotate-180" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto sidebar-scroll">
        {NAV_ITEMS.map((item) => (
          <RailItem key={item.href} {...item} showLabel={expanded} />
        ))}

        {/* Resume builder */}
        <div className="pt-4 border-t border-gray-200 mt-4">
          <button
            onClick={() => navigate("/resume-builder")}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-[#6366F1] hover:bg-[#EEF2FF] font-semibold",
              "border border-[#6366F1]/20 hover:border-[#6366F1]/40",
            )}
            title={!expanded ? "Resume Builder" : undefined}
          >
            <FileEdit className="h-4 w-4 flex-shrink-0" />
            <AnimatePresence>
              {expanded && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-sm font-semibold whitespace-nowrap"
                >
                  Resume Builder
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      {/* Bottom items */}
      <div className="px-3 pb-3 border-t border-gray-200 pt-4 space-y-1">
        {BOTTOM_ITEMS.map((item) => (
          <RailItem key={item.href} {...item} showLabel={expanded} />
        ))}
      </div>

      {/* User */}
      <div className="p-3 border-t border-gray-200">
        <div className={cn("flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-gray-50 transition-colors", !expanded && "justify-center")}>
          <Avatar className="h-8 w-8 flex-shrink-0 rounded-xl border border-gray-200">
            {user?.profile && <AvatarImage src={user.profile} />}
            <AvatarFallback className="rounded-xl text-xs bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] text-white font-bold">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex-1 min-w-0 flex items-center justify-between"
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold text-gray-900 truncate leading-tight">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="font-mono text-[10px] text-gray-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={logout}
                  className="h-7 w-7 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.aside>
  );
}

/* ── Top command bar ── */
function CommandBar() {
  const location = useLocation();
  const currentPage = [...NAV_ITEMS, ...BOTTOM_ITEMS].find((i) => i.href === location.pathname)?.label ?? "Dashboard";

  return (
    <div
      className="h-14 flex items-center justify-between px-6 border-b border-gray-200 flex-shrink-0 bg-white"
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] text-gray-400 uppercase tracking-widest font-semibold">applyiq</span>
        <span className="text-gray-300">/</span>
        <span className="font-mono text-[10px] text-[#6366F1] uppercase tracking-widest font-bold">{currentPage}</span>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1">
        <button className="h-9 w-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
          <Search className="h-4 w-4" />
        </button>
        <button className="h-9 w-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
          <Bell className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/* ── Page transition ── */
const pageVariants = {
  initial: { opacity: 0, y: 6 },
  enter:   { opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } },
  exit:    { opacity: 0, transition: { duration: 0.12 } },
};

/* ── Mobile drawer ── */
function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: -240 }}
            animate={{ x: 0 }}
            exit={{ x: -240 }}
            transition={{ type: "spring", stiffness: 400, damping: 38 }}
            className="fixed top-0 left-0 h-full z-40 w-[240px] flex flex-col bg-white border-r border-gray-200 shadow-2xl"
          >
            <div className="flex items-center h-14 px-4 border-b border-gray-200">
              <div className="h-8 w-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] text-white shadow-sm">
                <span className="font-display font-black text-sm">IQ</span>
              </div>
              <span className="ml-3 font-display font-black text-base text-gray-900">
                APPLY<span className="text-[#6366F1]">IQ</span>
              </span>
            </div>
            <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto sidebar-scroll">
              {NAV_ITEMS.map((item) => (
                <RailItem key={item.href} {...item} showLabel={true} />
              ))}
              <div className="pt-4 border-t border-gray-200 mt-4">
                <button
                  onClick={() => { navigate("/resume-builder"); onClose(); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#6366F1] hover:bg-[#EEF2FF] border border-[#6366F1]/20 transition-all text-sm font-semibold"
                >
                  <FileEdit className="h-4 w-4" /> Resume Builder
                </button>
              </div>
            </nav>
            <div className="px-3 pb-3 border-t border-gray-200 pt-4 space-y-1">
              {BOTTOM_ITEMS.map((item) => (
                <RailItem key={item.href} {...item} showLabel={true} />
              ))}
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Avatar className="h-8 w-8 rounded-xl border border-gray-200">
                  {user?.profile && <AvatarImage src={user.profile} />}
                  <AvatarFallback className="rounded-xl text-xs bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] text-white font-bold">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-xs font-bold text-gray-900">{user?.firstName}</p>
                  <p className="font-mono text-[10px] text-gray-500">{user?.email?.slice(0, 16)}…</p>
                </div>
              </div>
              <button onClick={logout} className="h-8 w-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ── AppLayout ── */
export default function AppLayout() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop rail */}
      <div className="hidden md:flex h-full">
        <SideRail expanded={!sidebarCollapsed} onToggle={toggleSidebar} />
      </div>

      {/* Mobile drawer */}
      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} />

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <div
          className="md:hidden flex items-center h-14 px-4 border-b border-gray-200 gap-3 flex-shrink-0 bg-white"
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="h-9 w-9 flex items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <div className="space-y-1">
              <span className="block h-0.5 w-4 bg-current rounded-full" />
              <span className="block h-0.5 w-4 bg-current rounded-full" />
              <span className="block h-0.5 w-3 bg-current rounded-full" />
            </div>
          </button>
          <div className="flex items-center gap-0">
            <span className="font-display font-black text-base text-gray-900">APPLY</span>
            <span className="font-display font-black text-base text-[#6366F1]">IQ</span>
          </div>
        </div>

        {/* Desktop command bar */}
        <div className="hidden md:block">
          <CommandBar />
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              variants={pageVariants}
              initial="initial"
              animate="enter"
              exit="exit"
              className="h-full"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
